import { useCallback, useEffect, useMemo } from 'react';
import {
  ColorValues,
  Flex,
  Typography,
} from '@banhala/ui-kit';
import { bridge, localizeNumber } from 'shared/src/utils';
import type { GoodsOption } from 'shared/src/api';
import type { DeliveryType } from 'shared/src/api/goodsService/getGoodsOptions';
import { useIsAnonymousUser } from 'shared/src/hooks';

import { CouponStateTag, type CouponState } from '../CouponStateTag';
import { IconBrandIconThirdPL } from '../../../IconBrandIconThirdPL';
import { useGoodsOptionsPricing } from '../../queries/useGoodsOptionsPricing';
import { usePostBatchRedeemCoupon } from '../../queries/usePostBatchRedeemCoupon';
import { useLogDownloadCoupon } from '../../hooks/useLogCompleteCouponDownload';
import { useGetInlineOrderDeadlineText } from '../../hooks/useGetOrderDeadlineText';
import { InlineTodayDeliveryIcon } from './InlineTodayDeliveryIcon';

const InlineExpectedPriceRow = ({
  finalPrice,
  disabled,
}: {
  finalPrice: number;
  disabled?: boolean;
}) => {
  const color = disabled ? 'content_tertiary' : 'content_primary';
  return (
    <Flex.Row justifyContent={'space-between'} alignItems={'center'}>
      <Typography type={'subtitle1'} color={color}>
        예상 결제금액
      </Typography>
      <Typography type={'h4'} color={color}>
        {localizeNumber(finalPrice)}원
      </Typography>
    </Flex.Row>
  );
};

const InlineTodayInfoView = ({
  orderDeadline,
  deliveryType,
}: {
  orderDeadline: { text: string; isTimeOver: boolean };
  deliveryType: DeliveryType;
}) => {
  const isThirdPL = deliveryType === 'third_pl';
  const badgeBackgroundColor = isThirdPL
    ? orderDeadline.isTimeOver
      ? 'pink5'
      : 'bg_pink_primary'
    : orderDeadline.isTimeOver
      ? 'bg_lilac_primary'
      : 'lilac40';
  const badgeIconColor = isThirdPL
    ? orderDeadline.isTimeOver
      ? ColorValues.pink30
      : ColorValues.white
    : orderDeadline.isTimeOver
      ? ColorValues.lilac40
      : ColorValues.white;
  const textType = orderDeadline.isTimeOver ? 'body4' : 'subtitle4';
  const textRawColor = isThirdPL
    ? orderDeadline.isTimeOver
      ? undefined
      : ColorValues.pink30
    : orderDeadline.isTimeOver
      ? undefined
      : ColorValues.lilac40;

  return (
    <Flex.Row alignItems={'center'}>
      <Flex
        width={54}
        height={18}
        borderRadius={9}
        backgroundColor={badgeBackgroundColor}
        alignItems={'center'}
        justifyContent={'center'}
      >
        {isThirdPL ? (
          <IconBrandIconThirdPL width={41} height={11} color={badgeIconColor} />
        ) : (
          <InlineTodayDeliveryIcon color={badgeIconColor} />
        )}
      </Flex>
      <Flex margin={{ left: 4 }} flexShrink={1}>
        {textRawColor ? (
          <Typography type={textType} rawColor={textRawColor} numberOfLines={1}>
            {orderDeadline.text}
          </Typography>
        ) : (
          <Typography
            type={textType}
            color={'content_slate_primary'}
            numberOfLines={1}
          >
            {orderDeadline.text}
          </Typography>
        )}
      </Flex>
    </Flex.Row>
  );
};

export const InlinePriceSummary = ({
  selected,
  count,
  isAllSelected,
  isRestockMode,
  deliveryType,
}: {
  selected: GoodsOption[];
  count: number;
  isAllSelected: boolean;
  isRestockMode: boolean;
  deliveryType: DeliveryType | undefined;
}) => {
  const isAnonymousUser = useIsAnonymousUser();
  const getDeadlineText = useGetInlineOrderDeadlineText();
  const logCouponDownloadComplete = useLogDownloadCoupon();

  const pricingOptions = useMemo(
    () =>
      selected.map(option => ({
        sno: option.sno,
        count: option.purchase_count ?? 1,
      })),
    [selected],
  );
  const { data: pricing, isLoading: loadingPricing } = useGoodsOptionsPricing({
    options: pricingOptions,
  });
  const {
    mutateAsync: postBatchRedeemCoupon,
    isPending: loadingPostBatchRedeemCoupon,
  } = usePostBatchRedeemCoupon(selected);

  const summary = useMemo(() => {
    const couponList = pricing?.coupon_list ?? [];
    const hasCoupons = couponList.length > 0;
    const allIssued = hasCoupons && couponList.every(c => c.item.is_issued);
    const totalDiscount = pricing?.discount_amount ?? 0;
    const couponState: CouponState = isAnonymousUser
      ? 'anonymous'
      : loadingPricing || loadingPostBatchRedeemCoupon
        ? 'loading'
        : !hasCoupons
          ? 'none'
          : allIssued
            ? 'issued'
            : 'downloadable';
    const showCouponRow = isAllSelected && !isRestockMode && hasCoupons;
    const unitPrice = selected[0]?.price ?? 0;
    const originalPrice = isAllSelected ? unitPrice * count : 0;
    const appliedDiscount = allIssued ? totalDiscount : 0;
    const finalPrice = Math.max(0, originalPrice - appliedDiscount);
    const restockPrice = unitPrice * count;

    return {
      allIssued,
      couponList,
      couponState,
      finalPrice,
      originalPrice,
      restockPrice,
      showCouponRow,
      totalDiscount,
    };
  }, [
    count,
    isAllSelected,
    isAnonymousUser,
    isRestockMode,
    loadingPostBatchRedeemCoupon,
    loadingPricing,
    pricing,
    selected,
  ]);

  const {
    allIssued,
    couponList,
    couponState,
    finalPrice,
    originalPrice,
    restockPrice,
    showCouponRow,
    totalDiscount,
  } = summary;

  const orderDeadline = deliveryType
    ? getDeadlineText({ delivery_type: deliveryType })
    : { text: '', isTimeOver: false };
  const showDeliveryInfo =
    isAllSelected &&
    !isRestockMode &&
    (deliveryType === 'shak' ||
      deliveryType === 'today' ||
      deliveryType === 'third_pl') &&
    orderDeadline.text.length > 0;

  const onPressRedeemCoupon = useCallback(async () => {
    if (isAnonymousUser) {
      bridge.assertLogin({
        message: '로그인을 해야 이용 할 수 있습니다.',
      });
      return;
    }

    if (!pricing || loadingPostBatchRedeemCoupon) return;
    const downloadableCoupons = pricing.coupon_list.filter(
      coupon => !coupon.item.is_issued,
    );

    if (downloadableCoupons.length === 0) return;

    try {
      await postBatchRedeemCoupon();
      downloadableCoupons.forEach(coupon => {
        logCouponDownloadComplete({ coupon, isDownloadAll: true });
      });
      bridge.toast('쿠폰이 발급되었습니다');
    } catch (error) {
      const ex = error as Error;
      bridge.toast(ex.message);
    }
  }, [
    isAnonymousUser,
    loadingPostBatchRedeemCoupon,
    logCouponDownloadComplete,
    postBatchRedeemCoupon,
    pricing,
  ]);

  useEffect(() => {
    if (loadingPricing || !pricing) return;
    if (pricing.coupon_list.length === 0) return;
    if (allIssued) return;

    bridge.logAnalyticsEvent({
      name: 'DISPLAY_COUPON',
      analytics: {
        ...pricing.coupon?.logging.analytics,
        SCREEN: 'GOODS_DETAIL',
      },
    });
  }, [allIssued, loadingPricing, pricing]);

  return (
    <Flex
      padding={{ horizontal: 16, vertical: 16 }}
      backgroundColor={'bg_always_white'}
    >
      {showDeliveryInfo && deliveryType && (
        <InlineTodayInfoView
          orderDeadline={orderDeadline}
          deliveryType={deliveryType}
        />
      )}
      {showCouponRow && (
        <Flex margin={{ top: showDeliveryInfo ? 16 : 0 }} gap={8}>
          <Flex.Row justifyContent={'space-between'} alignItems={'center'}>
            <Typography type={'body2'} color={'content_tertiary'}>
              총 {count}개 상품
            </Typography>
            <Typography type={'body2'} color={'content_primary'}>
              {localizeNumber(originalPrice)}원
            </Typography>
          </Flex.Row>
          <Flex.Row justifyContent={'space-between'} alignItems={'center'}>
            <Typography type={'body2'} color={'content_tertiary'}>
              쿠폰 할인
            </Typography>
            <CouponStateTag
              state={couponState}
              couponCount={couponList.length}
              discount={totalDiscount}
              onPressDownload={onPressRedeemCoupon}
            />
          </Flex.Row>
        </Flex>
      )}
      {showCouponRow && (
        <Flex margin={{ top: 12 }} height={1} backgroundColor={'bg_tertiary'} />
      )}
      {isAllSelected && (
        <Flex
          margin={{
            top: showCouponRow ? 12 : showDeliveryInfo ? 16 : 0,
          }}
        >
          <InlineExpectedPriceRow
            finalPrice={isRestockMode ? restockPrice : finalPrice}
            disabled={isRestockMode}
          />
        </Flex>
      )}
    </Flex>
  );
};
